import {
  Box,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { convertFiltersToURLSearchParams } from "../features/dashboard/utils";
import { UnprocessedCallsByReason } from "../types/metrics";
import { formatDuration } from "../utils/date";

interface UnprocessedReasonsTableProps {
  timeRange: string;
  protocol: string;
  user: string;
  callType: string;
  unprocessedCalls: UnprocessedCallsByReason[];
  isLoading: boolean;
}

const REASON_CODE_LABELS: Record<string, string> = {
  REASON_CODE_OK: "OK",
  REASON_CODE_SHORT_CALL: "Short Call",
  REASON_CODE_VOICEMAIL_REACHED: "Voicemail Reached",
  REASON_CODE_UNWANTED_CALL: "Unwanted Call",
  REASON_CODE_AUDIO_ISSUES: "Audio Issues",
  REASON_CODE_SINGLE_PARTICIPANT: "Single Participant",
  REASON_CODE_CALLBACK: "Callback",
  REASON_CODE_PERSONAL_CALL: "Personal Call",
  REASON_CODE_ONE_TIME_PASSWORD: "One-Time Password"
};

const formatReasonLabel = (reason: string): string => {
  return REASON_CODE_LABELS[reason] || reason.replace("REASON_CODE_", "").replace(/_/g, " ");
};

const UnprocessedReasonsTable = ({
  timeRange,
  protocol,
  user,
  callType,
  unprocessedCalls,
  isLoading
}: UnprocessedReasonsTableProps) => {
  const navigate = useNavigate();

  const handleReasonClick = (reason: string) => {
    const dashboardFilters = {
      timeRange,
      sentiment: "all",
      relevance: "all",
      risk: "all",
      protocol,
      user,
      processDecision: "DO_NOT_PROCESS",
      callType
    };

    const searchParams = convertFiltersToURLSearchParams(dashboardFilters);
    searchParams.set("pp_reason_code", reason);

    navigate(`/explore?${searchParams.toString()}`);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Reasons for Unprocessed Calls
      </Typography>

      {isLoading ? (
        <Box>
          {[1, 2, 3].map((index) => (
            <Skeleton key={index} height={40} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reason</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Total Duration</TableCell>
                <TableCell align="right">Avg Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {unprocessedCalls.map((row) => (
                <TableRow
                  key={row.reason}
                  onClick={() => handleReasonClick(row.reason)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover"
                    }
                  }}
                >
                  <TableCell component="th" scope="row">
                    {formatReasonLabel(row.reason)}
                  </TableCell>
                  <TableCell align="right">{row.count.toLocaleString()}</TableCell>
                  <TableCell align="right">{formatDuration(row.totalDurationMs)}</TableCell>
                  <TableCell align="right">{formatDuration(row.averageDurationMs)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default UnprocessedReasonsTable;
